/**
 * Settings > Integrations pieces shared by every integration tab (option A, approved 2026-10-29,
 * outputs/integrations-m365-mockup-20261029.html): the company picker, the "Setup guide" button
 * (a Vault document the company links; it opens the current approved version), the picker that
 * links it, and the "More" menu that holds the less-used actions.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api, type DocView } from "../../lib/api";
import { useStore } from "../../lib/store";
import { coName } from "../../lib/brand";
import { Tile } from "../../components/bits";
import { Icon } from "../../components/Icon";

type Guides = Awaited<ReturnType<typeof api.integrationGuides>>;

/** One company's setup-guide links, with save. */
export function useGuides(company: string) {
  const { toast } = useStore();
  const [g, setG] = useState<Guides | null>(null);
  const load = () => (company ? api.integrationGuides(company).then(setG, () => setG(null)) : Promise.resolve());
  useEffect(() => { setG(null); void load(); }, [company]);
  const save = async (kind: string, document_id: string | null) => {
    try { await api.setIntegrationGuide({ company, kind, document_id }); await load(); toast(document_id ? "Setup guide linked." : "Setup guide link removed."); return true; }
    catch (e) { toast((e as Error).message); return false; }
  };
  return { g, save };
}

/** "Setup guide" when one is linked (opens it); "Link setup guide" for admins when none is. */
export function GuideButton({ g, kind, onLink }: { g: Guides | null; kind: string; onLink: () => void }) {
  if (!g) return null;
  const cur = g.guides[kind];
  if (cur) return <Link className="btn btn-ghost btn-sm" to={`/doc/${cur.document_id}`} title={`${cur.number} ${cur.title}${cur.published ? "" : " (not approved yet)"}`}><Icon name="book" size={14} />Setup guide</Link>;
  return g.can_manage ? <button className="btn btn-ghost btn-sm" onClick={onLink}>Link setup guide</button> : null;
}

/** Find a Vault document by number or title and link it. */
export function GuidePicker({ company, label, current, onPick, onRemove, onCancel }: {
  company: string; label: string; current?: { number: string; title: string } | null; onPick: (id: string) => void; onRemove: () => void; onCancel: () => void;
}) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<DocView[]>([]);
  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return; }
    const h = setTimeout(() => {
      api.searchDocuments(q.trim()).then((r) => setHits(r.documents.filter((d) => (d.company === company || d.company === "CPY") && d.status !== "archived" && d.status !== "retired").slice(0, 8)), () => setHits([]));
    }, 250);
    return () => clearTimeout(h);
  }, [q, company]);
  return (
    <div className="sg-pick">
      <div><b>Setup guide for {label}</b>{current ? <span className="cap"> · now <span className="mono">{current.number}</span> {current.title}</span> : null}</div>
      <div className="cap">The Vault document {coName(company)} follows. Pick a {coName(company)} or company-wide document; the button always opens its current approved version.</div>
      <div className="row-btns" style={{ margin: "8px 0 0" }}>
        <input className="inp inp-sm" autoFocus placeholder="Find by number or title" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find a document" style={{ maxWidth: 340 }} />
        {current && <button className="linkbtn" onClick={onRemove}>Remove link</button>}
        <button className="linkbtn" onClick={onCancel}>Cancel</button>
      </div>
      {hits.length > 0 && <ul className="sg-hits">{hits.map((d) => (
        <li key={d.id}><button onClick={() => onPick(d.id)}><span className="mono">{d.base_number}</span> {d.title}{d.status !== "published" ? <span className="cap"> · not approved yet</span> : null}</button></li>))}</ul>}
      {q.trim().length >= 2 && !hits.length && <div className="cap" style={{ marginTop: 6 }}>No matching documents.</div>}
    </div>
  );
}

/** "More" button with a small menu for the less-used actions. */
export function MoreMenu({ items }: { items: { label: string; onClick: () => void; danger?: boolean }[] }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    document.addEventListener("click", close); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("click", close); document.removeEventListener("keydown", esc); };
  }, [open]);
  if (!items.length) return null;
  return (
    <span className="mm">
      <button className="btn btn-ghost btn-sm" aria-haspopup="menu" aria-expanded={open} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>More</button>
      {open && <div className="mm-pop" role="menu" onClick={(e) => e.stopPropagation()}>
        {items.map((i) => <button key={i.label} role="menuitem" className={i.danger ? "danger" : ""} onClick={() => { setOpen(false); i.onClick(); }}>{i.label}</button>)}
      </div>}
    </span>
  );
}

/** Company chips: one company at a time, with a one-line status under each name. */
export function CompanyChips({ codes, co, pick, sub }: { codes: string[]; co: string; pick: (c: string) => void; sub: (c: string) => ReactNode }) {
  if (codes.length < 2) return null;
  return (
    <div className="ig-cos" role="tablist" aria-label="Company">
      {codes.map((c) => (
        <button key={c} role="tab" aria-selected={c === co} className={`ig-co ${c === co ? "on" : ""}`} onClick={() => pick(c)}>
          <Tile code={c} /><span><b>{coName(c)}</b><small>{sub(c)}</small></span>
        </button>
      ))}
    </div>
  );
}
